import React from "react";
import { cn } from "../../../common/u1/cn";
import { ChevronDown } from "../../../../assets/ui/icons";

/* The profile's four sections, as the design's numbered strip. */
export const PROFILE_TABS = [
  {
    id: 1,
    ordinal: "01",
    label: "General Information",
    hint: "Identity, contacts, signatories",
  },
  {
    id: 2,
    ordinal: "02",
    label: "Business Details",
    hint: "Lanes, ownership, UBO",
  },
  {
    id: 4,
    ordinal: "03",
    label: "Financials",
    hint: "Banking, revenue, audits",
  },
  {
    id: 3,
    ordinal: "04",
    label: "Documents",
    hint: "Licence, VAT, IDs",
  },
];

/* How many columns the strip uses, by how many tabs there actually are. */
const COLUMNS = {
  1: "sm:grid-cols-1 lg:grid-cols-1",
  2: "sm:grid-cols-2 lg:grid-cols-2",
  3: "sm:grid-cols-2 lg:grid-cols-3",
  4: "sm:grid-cols-2 lg:grid-cols-4",
};

/* A tab reports whether it is locked so the strip can say so rather than
 * looking broken when a click does nothing. The live screen swaps the panel
 * beneath for a lock card, which is preserved — this only marks the tab. */
export const ProfileTabs = ({
  tabs = PROFILE_TABS,
  active = 1,
  onChange = () => {},
  isLocked = () => false,
  className = "",
}) => (
  <div
    role="tablist"
    aria-label="Company profile sections"
    className={cn(
      "grid grid-cols-1 overflow-hidden rounded-t-[14px] border border-[var(--line)]",
      "border-b-0 bg-white",
      COLUMNS[tabs.length] || COLUMNS[4],
      className
    )}
  >
    {tabs.map((tab) => {
      const selected = active === tab.id;
      const locked = isLocked(tab.id);

      return (
        <button
          key={tab.id}
          type="button"
          role="tab"
          id={`u1-tab-${tab.id}`}
          aria-selected={selected}
          aria-controls={`u1-panel-${tab.id}`}
          onClick={() => onChange(tab.id)}
          className={cn(
            "relative flex flex-col items-start gap-1 px-5 py-4 text-left transition-colors",
            /* The coral top edge marks the open section, as the design draws
               it. Drawn on every tab and made transparent otherwise, so the
               row's height does not shift when the selection moves. */
            "border-t-[3px]",
            selected
              ? "border-t-[var(--coral)] bg-[var(--bg)]"
              : "border-t-transparent bg-white hover:bg-[#002f490b]",
            /* The rules between tabs, dropped on the last of each row. */
            "border-r border-r-[var(--line)] last:border-r-0",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--coral)]"
          )}
        >
          <span className="flex w-full items-center gap-2">
            <span
              className={cn(
                "text-[11px] font-bold tracking-[.1em] font-mono",
                selected ? "text-[var(--coral)]" : "text-[var(--muted-2)]"
              )}
            >
              {tab.ordinal}
            </span>
            {!selected && (
              <span className="ml-auto shrink-0 text-[var(--muted-2)]">
                <ChevronDown />
              </span>
            )}
          </span>

          <span className="text-[15px] font-bold text-[var(--ink)] font-sans">
            {tab.label}
          </span>
          <span className="text-[12px] text-[var(--muted)] font-sans">
            {locked ? "Sign in or buy access to open" : tab.hint}
          </span>
        </button>
      );
    })}
  </div>
);

export default ProfileTabs;
